import dynamic from 'next/dynamic';
import { useState } from 'react';
import styled from 'styled-components';

import { Image } from 'shared/components/Image';
import { IProductImage } from 'shared/types/ProductImage';
import { normalizeValue } from 'shared/utils/normalizeValue';
import { setWindowFocus } from 'store/slice/pdpStateSlice';
import { t } from 'localization/utils/t';
import { useStoreDispatch, useStoreSelector } from 'store/hooks/storeHooks';

const TP = 'productTemplate.components.buyBar.GalleryItemVariant';

interface IGalleryItemVariantProps {
  idx?: number;
  item?: IProductImage;
  itemList?: { mainPictureUrl: string }[];
  variantId: string;
}

const GalleryItemExpandedView = dynamic(
  () => import('productTemplate/components/imageGallery/GalleryItemExpandedView'),
);

export const GalleryItemVariant = ({
  idx,
  item,
  itemList,
  variantId,
}: IGalleryItemVariantProps) => {
  const {
    name,
    color: productColor,
    productType,
  } = useStoreSelector((state) => state.pdp.productTemplate);
  const storeDispatch = useStoreDispatch();
  const { focusedWindow } = useStoreSelector((state) => state.pdpState);
  const [isExpanded, setIsExpanded] = useState(false);
  const [clickTargetIdx, setClickTargetIdx] = useState<number>(0);
  const shouldCropImage = !(productType === 'tops' && productColor?.toLowerCase() === 'white');

  const handleClick = (shouldExpand) => {
    if (focusedWindow === variantId) {
      setClickTargetIdx(idx);
      setIsExpanded(shouldExpand);
    } else {
      storeDispatch(setWindowFocus(variantId));
    }
  };

  const close = () => setIsExpanded(false);

  return (
    <GalleryItemVariant.Wrapper data-qa="gallery_item_used">
      <Image
        alt={normalizeValue(name)}
        onClick={() => handleClick(true)}
        sizes="250px"
        src={item.url || ''}
        crop
        disableLazyLoad
      />
      {isExpanded && (
        <GalleryItemExpandedView
          clickTargetIdx={clickTargetIdx}
          itemList={itemList}
          name={name}
          shouldCropImage={shouldCropImage}
          title={t(`${TP}.fromSeller`, 'From Seller')}
          onClose={close}
        />
      )}
    </GalleryItemVariant.Wrapper>
  );
};

GalleryItemVariant.Wrapper = styled.div`
  min-height: 150px;
  > img {
    object-fit: contain;
    height: inherit;
    cursor: pointer;
  }
`;
